import axios, { AxiosInstance } from 'axios';

import { attachSessionRefreshInterceptors } from 'services/sessionRefreshInterceptor';
import { getAccessToken } from 'utils/sessionTokens';

import type { SnapshotCreateBody } from './types';

const baseUrl = () => (process.env.REACT_APP_MAESTRO_API_URL || '').replace(/\/$/, '');

let client: AxiosInstance | null = null;

const http = (): AxiosInstance => {
  const baseURL = baseUrl();
  if (!baseURL) {
    throw new Error('REACT_APP_MAESTRO_API_URL is not set.');
  }
  if (!client) {
    client = axios.create({ baseURL, timeout: 120_000 });
    client.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (token && config.headers && !config.headers.authorization) {
        config.headers.authorization = token;
      }
      return config;
    });
    attachSessionRefreshInterceptors(client);
  }
  return client;
};

export const maestroErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { detail?: unknown; messages?: string[] } } })
    ?.response?.data;
  if (data?.messages?.length) return data.messages.join(' ');
  if (typeof data?.detail === 'string' && data.detail) return data.detail;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export type DeliveryStatus = 'draft' | 'active' | 'paused';

export interface DeliveryRecipient {
  type: 'user' | 'email';
  user_id?: number;
  address?: string;
}

export interface DeliverySchedule {
  frequency?: string;
  day_of_week?: number;
  time_utc?: string;
  /** UTC cron (daily / weekly / monthly presets). Used by the future schedule worker. */
  cron?: string;
  /** Display label for a known preset. */
  label?: string;
}

export interface DeliveryRecord {
  id: string;
  organization_id: number;
  model_id: number;
  owner_user_id: number;
  name: string;
  status: DeliveryStatus;
  source_ref: { service: string; type: string; id: string };
  parameter_set: SnapshotCreateBody;
  output_types: string[];
  recipients: DeliveryRecipient[];
  schedule: DeliverySchedule;
  created_at: string;
  updated_at: string;
}

export interface DeliveryRunRecord {
  id: string;
  delivery_id: string;
  trigger: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  snapshot_id: string | null;
  artifact_id: string | null;
  recipients_sent: Array<Record<string, unknown>>;
  error: string | null;
}

export interface DeliveryUpsertBody {
  model_id: number;
  name?: string;
  status?: DeliveryStatus;
  source_ref: { service: 'blox-bi'; type: 'dashboard'; id: string };
  parameter_set: SnapshotCreateBody;
  output_types?: string[];
  recipients: DeliveryRecipient[];
  schedule: DeliverySchedule;
}

export const listDeliveries = async (modelId: number): Promise<DeliveryRecord[]> => {
  const response = await http().get<{ deliveries: DeliveryRecord[] }>('/deliveries', {
    params: { model_id: modelId },
  });
  return response.data.deliveries ?? [];
};

export const createDelivery = async (body: DeliveryUpsertBody): Promise<DeliveryRecord> => {
  const response = await http().post<DeliveryRecord>('/deliveries', body);
  return response.data;
};

export const updateDelivery = async (
  deliveryId: string,
  body: Partial<DeliveryUpsertBody>,
): Promise<DeliveryRecord> => {
  const response = await http().patch<DeliveryRecord>(`/delivery/${deliveryId}`, body);
  return response.data;
};

export const testDelivery = async (deliveryId: string): Promise<DeliveryRunRecord> => {
  const response = await http().post<DeliveryRunRecord>(`/delivery/${deliveryId}/test`);
  return response.data;
};

export const listDeliveryRuns = async (deliveryId: string): Promise<DeliveryRunRecord[]> => {
  const response = await http().get<{ runs: DeliveryRunRecord[] }>(`/delivery/${deliveryId}/runs`);
  return response.data.runs ?? [];
};
